import React, {
  ChangeEvent,
  DragEvent,
  FC,
  FormEvent,
  useCallback,
  useReducer,
  useRef,
  useState,
} from "react";
import Button from "components/Button/Button";
import FormError from "components/FormError/FormError";
import { TextAreaField } from "components/TextArea/TextArea.styles";
import {
  BackArrow,
  ContentWrapper,
  Heading,
} from "containers/RightPanel/RightPanel.styles";
import { ButtonColor, ButtonType, ButtonVariant } from "enums/Button";
import { IconType } from "enums/Icon";
import { useAgentJobRun } from "hooks/useAgentJobRun";
import { fromJS, Map } from "immutable";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import {
  addAddress,
  deleteAddress,
  getAddresses,
} from "reduxFolder/reducers/destinations";
import {
  closeRightPanel,
  openRightPanel,
} from "reduxFolder/reducers/rightPanel";
import { Label, LabelText } from "styles/formStyles";
import { AddressParseJobDto } from "types/api/addressParseJobs";
import { DestinationAddressResponseDto } from "types/api/destination_addresses";
import { CloseRightPanel, OpenRightPanel } from "types/common/rightPanel";
import {
  buildAddressParseFormData,
  createAddressParseJob,
  PHOTO_ACCEPT,
  pollAddressParseJob,
  validatePhoto,
} from "utils/addressParseJob";
import {
  toastResponseError,
  toastResponseSuccess,
} from "utils/responseMessageHelper";
import { AddButton } from "../Addresses.style";
import { leaveWithAddress } from "../addressFormHelper";
import {
  canSubmit,
  Failure,
  fieldLabelKey,
  initialPhase,
  reduce,
} from "../addressParseHelper";
import {
  ButtonsWrapper,
  Content,
  FormWrapper,
  InfoText,
  InfoWrapper,
} from "./AddressForm.styles";
import AddressItem from "./AddressItem";
import {
  CardWrapper,
  Detail,
  DetailsList,
  DropZone,
  DropZoneHint,
  DropZoneIcon,
  DropZoneText,
  FileName,
  Hint,
  ManualRow,
  ReviewMeta,
  Spinner,
  SpinnerHolder,
  StatusBlock,
  StatusText,
  TextButton,
} from "./AddressParsePanel.styles";

// Free-form "Adding Address" panel (ADDRESS_PARSE): the customer pastes the
// address as text and/or attaches a photo, the backend parse job recognizes
// it and creates the address, and the customer reviews it here. Editing and
// "Fill in manually" hand over to the static ADDRESS_FORM with the same
// panelData contract ({ isEditForm, isNewAddress, initialPanelData,
// returnPreviousPanel, onAddressAdded }). "Use this address" (or saving in the
// form) hands the address to the host that opened the panel — see
// leaveWithAddress.
interface AddressParsePanelProps {
  panelData: Map<string, any>;
  isOpen: boolean;
  openRightPanel: OpenRightPanel;
  closeRightPanel: CloseRightPanel;
  addAddress: (address: DestinationAddressResponseDto) => void;
  deleteAddress: (data: { id: number }) => Promise<unknown>;
  getAddresses: () => Promise<{ value: Map<string, any> }>;
}

const HINT_ID = "address-parse-hint";

export const AddressParsePanel: FC<AddressParsePanelProps> = ({
  panelData,
  isOpen,
  openRightPanel,
  closeRightPanel,
  addAddress,
  deleteAddress,
  getAddresses,
}) => {
  const { t } = useTranslation("common");
  const [phase, dispatch] = useReducer(reduce, initialPhase);
  const [rawInput, setRawInput] = useState("");
  const [photo, setPhoto] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const returnPreviousPanel = panelData.get("returnPreviousPanel");
  const initialPanelData = panelData.get("initialPanelData", Map());

  // Returns to the panel that opened this one, or closes the drawer.
  const leave = useCallback(() => {
    if (returnPreviousPanel) {
      openRightPanel(returnPreviousPanel, initialPanelData);
    } else {
      closeRightPanel();
    }
  }, [returnPreviousPanel, initialPanelData, openRightPanel, closeRightPanel]);

  const openManualForm = useCallback(
    () => openRightPanel("ADDRESS_FORM", panelData),
    [openRightPanel, panelData],
  );

  // Closing the panel aborts the poll and drops any result that lands late.
  const { start, keepWaiting, stop } = useAgentJobRun<
    AddressParseJobDto,
    "parsed" | "failed"
  >({
    poll: (jobId, signal) => pollAddressParseJob(jobId, { signal }),
    isActive: isOpen,
    dispatch,
    onDone: (job, signal) => {
      const address = job.destination_address;
      if (address) {
        addAddress(address);
        dispatch({ type: "JOB_DONE", result: address });
        return;
      }
      // Backend without the embedded address: refresh the list so it
      // shows the new address, and review it from there if it came back.
      // Returned so closing the panel aborts `signal` while the list loads.
      const addressId = job.destination_address_id;
      const leaveWithToast = () => {
        toastResponseSuccess(t("addressParse.addedNotShown"));
        leave();
      };
      return getAddresses()
        .then(({ value }) => {
          if (signal.aborted) return;
          const found = value
            .get("data")
            .find((item: Map<string, any>) => item.get("id") === addressId);
          if (found) {
            dispatch({ type: "JOB_DONE", result: found.toJS() });
          } else {
            leaveWithToast();
          }
        })
        .catch(() => {
          if (!signal.aborted) leaveWithToast();
        });
    },
  });

  // Cancel and Back. Switching to another panel keeps this one mounted (still
  // isOpen) while the drawer content fades, so stop the run here, not at
  // unmount: a poll in flight is aborted and a job created late is not polled.
  const exit = useCallback(() => {
    stop();
    leave();
  }, [stop, leave]);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (phase.name !== "input") return;
      const valid = canSubmit(rawInput, photo);
      dispatch({ type: "SUBMIT", canSubmit: valid });
      if (!valid) return;

      start(() =>
        createAddressParseJob(buildAddressParseFormData(rawInput, photo)),
      );
    },
    [phase.name, rawInput, photo, start],
  );

  const acceptPhoto = useCallback(
    (file: File | null) => {
      if (!file) return;
      const problem = validatePhoto(file);
      if (problem) {
        toastResponseError(
          t(
            problem === "tooLarge"
              ? "addressParse.photoTooLarge"
              : "addressParse.photoWrongFormat",
          ),
        );
        return;
      }
      setPhoto(file);
    },
    [t],
  );

  const handlePhotoChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files ? event.target.files[0] : null;
      // reset synchronously so re-selecting the same file re-fires onChange
      event.target.value = "";
      acceptPhoto(file);
    },
    [acceptPhoto],
  );

  const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    // dragleave also fires when the pointer moves between the zone's children
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragging(false);
      acceptPhoto(event.dataTransfer?.files?.[0] ?? null);
    },
    [acceptPhoto],
  );

  const handleKeepWaiting = useCallback(() => {
    if (phase.name !== "timeout") return;
    keepWaiting(phase.jobId);
  }, [phase, keepWaiting]);

  // The form keeps our panelData (isNewAddress, return target, onAddressAdded),
  // so saving there hands the address over the same way "Use this address" does.
  const handleEdit = useCallback(() => {
    if (phase.name !== "review") return;
    openRightPanel(
      "ADDRESS_FORM",
      panelData.merge({
        address: fromJS(phase.result),
        isEditForm: true,
        addressID: phase.result.id,
      }),
    );
  }, [phase, openRightPanel, panelData]);

  const handleDiscard = useCallback(() => {
    if (phase.name !== "review") return;
    setIsDiscarding(true);
    deleteAddress({ id: phase.result.id })
      .then(() => dispatch({ type: "DISCARDED" }))
      .catch(toastResponseError)
      .finally(() => setIsDiscarding(false));
  }, [phase, deleteAddress]);

  const handleUse = useCallback(() => {
    if (phase.name !== "review") return;
    leaveWithAddress(panelData, phase.result, openRightPanel, closeRightPanel);
  }, [phase, panelData, openRightPanel, closeRightPanel]);

  const cancelButton = (
    <Button type={ButtonType.Button} onClick={exit}>
      {t("common.cancel")}
    </Button>
  );
  const manualButton = (primary = false) => (
    <Button
      type={ButtonType.Button}
      color={primary ? ButtonColor.Primary : undefined}
      variant={primary ? ButtonVariant.Filled : undefined}
      onClick={openManualForm}
    >
      {t("addressParse.enterManually")}
    </Button>
  );

  const renderInput = (error: "requiredAny" | null) => (
    <>
      <Label $invalid={!!error}>
        <LabelText>{t("addressParse.inputLabel")}</LabelText>
        <TextAreaField
          name="rawInput"
          $invalid={!!error}
          disabled={false}
          rows={5}
          placeholder={t("addressParse.inputPlaceholder")}
          aria-describedby={HINT_ID}
          value={rawInput}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
            setRawInput(event.target.value)
          }
        />
      </Label>
      <Hint id={HINT_ID}>{t("addressParse.inputHint")}</Hint>
      {error && <FormError error={t(`addressParse.${error}`)} />}
      <DropZone
        data-testid="photo-dropzone"
        $isDragging={isDragging}
        onDragEnter={handleDragOver}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={PHOTO_ACCEPT}
          hidden
          onChange={handlePhotoChange}
        />
        <DropZoneIcon type={IconType.AdditionalPhotos} />
        {photo ? (
          <>
            <FileName>{photo.name}</FileName>
            <TextButton type="button" onClick={() => setPhoto(null)}>
              {t("addressParse.removePhoto")}
            </TextButton>
          </>
        ) : (
          <>
            <DropZoneText>{t("addressParse.dropPhoto")}</DropZoneText>
            <Button
              type={ButtonType.Button}
              onClick={() => fileInputRef.current?.click()}
            >
              {t("addressParse.attachPhoto")}
            </Button>
            <DropZoneHint>{t("addressParse.photoFormats")}</DropZoneHint>
          </>
        )}
      </DropZone>
      <ManualRow>
        <AddButton
          type={ButtonType.Button}
          color={ButtonColor.Primary}
          onClick={openManualForm}
        >
          {t("addressParse.enterManually")}
        </AddButton>
      </ManualRow>
    </>
  );

  const renderFailure = (failure: Failure) => (
    <StatusBlock role="alert">
      <StatusText>{t("addressParse.failedTitle")}</StatusText>
      {failure.kind === "unavailable" && (
        <Hint>{t("addressParse.failedUnavailable")}</Hint>
      )}
      {failure.kind === "validation" && (
        <>
          <Hint>{t("addressParse.failedValidation")}</Hint>
          <DetailsList>
            {failure.fieldErrors.map(({ field, messages }) => (
              <li key={field}>
                {t(fieldLabelKey(field))}: {messages.join(", ")}
              </li>
            ))}
          </DetailsList>
        </>
      )}
      {failure.kind === "generic" && (
        <>
          <Hint>{t("addressParse.failedGeneric")}</Hint>
          {failure.detail && <Detail>{failure.detail}</Detail>}
        </>
      )}
    </StatusBlock>
  );

  const renderReview = (address: DestinationAddressResponseDto) => (
    <>
      <InfoWrapper>
        <InfoText>{t("addressParse.reviewHint")}</InfoText>
      </InfoWrapper>
      <CardWrapper>
        <AddressItem isHideButtons address={fromJS(address)} />
      </CardWrapper>
      {address.state && (
        <ReviewMeta>
          {t(fieldLabelKey("state", address.country))}: {address.state}
        </ReviewMeta>
      )}
    </>
  );

  const renderBody = () => {
    switch (phase.name) {
      case "input":
        return renderInput(phase.error);
      case "parsing":
        return (
          <StatusBlock role="status" aria-live="polite">
            <SpinnerHolder>
              <Spinner isActive />
            </SpinnerHolder>
            <StatusText>{t("addressParse.parsing")}</StatusText>
            <Hint>{t("addressParse.parsingCanLeave")}</Hint>
          </StatusBlock>
        );
      case "timeout":
        return (
          <StatusBlock role="status" aria-live="polite">
            <StatusText>{t("addressParse.timeoutTitle")}</StatusText>
            <Hint>{t("addressParse.parsingSlow")}</Hint>
          </StatusBlock>
        );
      case "failed":
        return renderFailure(phase.failure);
      case "review":
        return renderReview(phase.result);
    }
  };

  const renderButtons = () => {
    switch (phase.name) {
      case "input":
        return (
          <>
            {cancelButton}
            <Button
              type={ButtonType.Submit}
              color={ButtonColor.Primary}
              variant={ButtonVariant.Filled}
            >
              {t("addressParse.recognize")}
            </Button>
          </>
        );
      case "parsing":
        return cancelButton;
      case "timeout":
        return (
          <>
            {cancelButton}
            {manualButton()}
            <Button
              type={ButtonType.Button}
              color={ButtonColor.Primary}
              variant={ButtonVariant.Filled}
              onClick={handleKeepWaiting}
            >
              {t("addressParse.keepWaiting")}
            </Button>
          </>
        );
      case "failed":
        return phase.failure.kind === "unavailable" ? (
          <>
            {cancelButton}
            {manualButton(true)}
          </>
        ) : (
          <>
            {cancelButton}
            {manualButton()}
            <Button
              type={ButtonType.Button}
              color={ButtonColor.Primary}
              variant={ButtonVariant.Filled}
              onClick={() => dispatch({ type: "RETRY" })}
            >
              {t("addressParse.tryAgain")}
            </Button>
          </>
        );
      case "review":
        return (
          <>
            <Button
              type={ButtonType.Button}
              color={ButtonColor.Red}
              isLoading={isDiscarding}
              disabled={isDiscarding}
              onClick={handleDiscard}
            >
              {t("addressParse.discard")}
            </Button>
            <Button
              type={ButtonType.Button}
              disabled={isDiscarding}
              onClick={handleEdit}
            >
              {t("common.edit")}
            </Button>
            <Button
              type={ButtonType.Button}
              color={ButtonColor.Primary}
              variant={ButtonVariant.Filled}
              disabled={isDiscarding}
              onClick={handleUse}
            >
              {t("addressParse.useAddress")}
            </Button>
          </>
        );
    }
  };

  return (
    <ContentWrapper>
      <Heading>
        {returnPreviousPanel && (
          <button
            type="button"
            aria-label={t("common.back")}
            title={t("common.back")}
            onClick={exit}
          >
            <BackArrow type={IconType.Arrow} />
          </button>
        )}
        {t("account.addingAddress")}
      </Heading>
      <FormWrapper onSubmit={handleSubmit}>
        <Content>{renderBody()}</Content>
        <ButtonsWrapper>{renderButtons()}</ButtonsWrapper>
      </FormWrapper>
    </ContentWrapper>
  );
};

const withConnect = connect(null, {
  openRightPanel,
  closeRightPanel,
  addAddress,
  deleteAddress,
  getAddresses,
});

export default withConnect(AddressParsePanel);
